/**
 * Podcast Episodes Section - Data Fetcher and Transformation
 * Handles fetching podcast data from DatoCMS and transforming it for components
 */

import type {
  PodcastEpisode,
  DatoCMSPodcastEpisode,
  DatoCMSPodcastSection,
} from "./types";

/**
 * Transform raw DatoCMS podcast episode to component data
 * Handles data structure normalization (DatoCMS returns camelCase already)
 */
export function transformDatoCMSPodcastEpisode(
  episode: DatoCMSPodcastEpisode
): PodcastEpisode {
  return {
    id: episode.id,
    name: episode.name,
    podcastLength: episode.podcastLength,
    podcastItem: episode.podcastItem
      ? {
          id: episode.podcastItem.id,
          links: episode.podcastItem.links
            ? episode.podcastItem.links.map((link) => ({
                id: link.id,
                url: link.url,
                icon: link.icon
                  ? {
                      url: link.icon.url,
                      alt: link.icon.alt,
                      title: link.icon.title,
                    }
                  : undefined,
              }))
            : [],
        }
      : undefined,
  };
}

/**
 * Transform raw DatoCMS podcast section to component props
 * Applies transformation to all episodes in both columns
 */
export function transformDatoCMSPodcastSection(
  section: DatoCMSPodcastSection
): {
  latestEpisodes: PodcastEpisode[];
  topEpisodes: PodcastEpisode[];
} {
  return {
    latestEpisodes: (section.latestEpisodes || []).map(
      transformDatoCMSPodcastEpisode
    ),
    topEpisodes: (section.topEpisodes || []).map(
      transformDatoCMSPodcastEpisode
    ),
  };
}

/**
 * GraphQL Fragment for fetching podcast episode data
 * Include this in your DatoCMS GraphQL queries
 */
export const PODCAST_EPISODE_FRAGMENT = `
  fragment PodcastEpisodeFields on HomepagePodcastRecord {
    id
    name
    podcastLength
    podcastItem {
      id
      links {
        id
        url
        icon {
          url
          alt
          title
          blurUpThumb
        }
      }
    }
  }
`;

/**
 * Example GraphQL query for fetching homepage podcast section
 * Use this in your homepage fetcher
 */
export const HOMEPAGE_PODCAST_QUERY = `
  query HomepagePodcasts {
    homepage {
      latestEpisodes {
        ...PodcastEpisodeFields
      }
      topEpisodes {
        ...PodcastEpisodeFields
      }
    }
  }
  ${PODCAST_EPISODE_FRAGMENT}
`;
